/**
 * Helpers of a row facade's cell reads (`sheet-row-handle.ts`): a read owned
 * by two owners, the column a single-cell read names, the refusal of a
 * single-cell read on a row that turned multi-cardinality, and the alias
 * descendant of a rows-side row. Moved out of the facade module as written.
 *
 * @module
 */
import type { Option } from "@tento-lona/sheets";
import { NaiveDate } from "@tento-chrono";
import type { RowLocalId, Unsubscribe } from "@tento-lona/sheets";
import type { ModelRow } from "@tento-lona/sheets";
import { SdkRequestError } from "../errors.ts";

/**
 * A read's owner, as `CellHandle.Owner` declares it, spelled here so this
 * module needs nothing of the cell handle (which reaches the facade).
 */
type ReadOwner = {
  assertLive(): void;
  watch(onDisposed: (err: Error) => void): Unsubscribe;
};

/** A read owned by both `first` and `second`: live while both are, failed
 *  once — by whichever is disposed first — and released from both. */
export function bothOwners(first: ReadOwner, second: ReadOwner): ReadOwner {
  return {
    assertLive: () => {
      first.assertLive();
      second.assertLive();
    },
    watch: (onDisposed) => {
      let fired = false;
      const once = (err: Error): void => {
        if (fired) return;
        fired = true;
        onDisposed(err);
      };
      const unwatchFirst = first.watch(once);
      const unwatchSecond = second.watch(once);
      return () => {
        unwatchFirst();
        unwatchSecond();
      };
    },
  };
}

/**
 * The refusal of a single-cell read (`verb`) on row `id` once its cell
 * handle found it multi-cardinality (its metadata said so after the single
 * facade was handed out): a column names none of its cells, so the read
 * says so instead of answering an empty column.
 */
export function turnedMulti(id: RowLocalId, verb: "row.peek" | "row.get"): SdkRequestError {
  return new SdkRequestError({
    boundary: "cells",
    step: "get-cells",
    method: "GET",
    path: "/api/rows/{rowKey}/cells",
    failure: { outcome: "unaddressable" },
    details: `${verb}: row ${id.toString()} holds several cells per column (told apart by id): ` +
      "read them with row.cells(range)",
  });
}

/** The column a single-cell read names: a day's, or the partial itself. */
export function columnOf(day: NaiveDate | NaiveDate.Partial): NaiveDate.Partial {
  return day instanceof NaiveDate.Partial ? day : NaiveDate.Partial.fromDate(day);
}

/** The first row in `row`'s subtree (itself included) that is an alias of `template`. */
export function findRowsSideAliasDescendant(
  row: ModelRow,
  template: string,
): Option<ModelRow> {
  if (row.descriptor.role === "alias" && row.descriptor.template === template) {
    return row;
  }
  for (const child of row.children) {
    const found = findRowsSideAliasDescendant(child, template);
    if (found) return found;
  }
  return null;
}
